// src/products/analysis/pages/StockChartPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getStockCandles, getTimePeriodTimestamps } from '../services/finnhubService';

const StockChartPage = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('90d');
  const [companyName, setCompanyName] = useState(symbol);

  const timePeriods = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '60d', label: '60 Days' },
    { value: '90d', label: '90 Days' },
    { value: '180d', label: '180 Days' },
    { value: '1y', label: '1 Year' },
    { value: '5y', label: '5 Years' }
  ];

  useEffect(() => {
    if (symbol) {
      loadChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, selectedPeriod]);

  const loadChartData = async () => {
    if (!symbol) return;
    
    setLoading(true);
    setError(null);

    try {
      const { from, to } = getTimePeriodTimestamps(selectedPeriod);
      
      // Determine resolution based on period
      let resolution = 'D'; // Daily by default
      if (selectedPeriod === '5y') {
        resolution = 'W'; // Weekly for 5 years
      }

      // Ensure symbol is uppercase and clean
      const cleanSymbol = symbol.toUpperCase().trim();
      
      const candles = await getStockCandles(cleanSymbol, resolution, from, to);
      
      if (!candles || candles.length === 0) {
        setError(`No data available for ${cleanSymbol} for this time period. Please try a different period.`);
        setChartData([]);
      } else {
        // Filter data to match selected period
        const filteredCandles = filterCandlesByPeriod(candles, selectedPeriod);
        setChartData(filteredCandles);
        setCompanyName(cleanSymbol);
      }
    } catch (err) {
      let errorMessage = err.message || 'Failed to load chart data. Please try again.';
      
      // Make error messages more user-friendly
      if (errorMessage.includes('403') || errorMessage.includes('premium')) {
        errorMessage = 'Chart data requires a premium API subscription. Free tier has limited access. Please try a shorter time period (7-90 days) or consider upgrading your API plan.';
      } else if (errorMessage.includes('rate limit')) {
        errorMessage = 'API rate limit exceeded. Please wait a minute and try again.';
      }
      
      setError(errorMessage);
      console.error('Chart loading error:', err);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter candles based on selected period (for Alpha Vantage which returns all data)
  const filterCandlesByPeriod = (candles, period) => {
    if (!candles || candles.length === 0) return candles;
    
    const now = Date.now();
    const periodMs = {
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000,
      '60d': 60 * 24 * 60 * 60 * 1000,
      '90d': 90 * 24 * 60 * 60 * 1000,
      '180d': 180 * 24 * 60 * 60 * 1000,
      '1y': 365 * 24 * 60 * 60 * 1000,
      '5y': 5 * 365 * 24 * 60 * 60 * 1000
    };
    
    const cutoff = now - (periodMs[period] || periodMs['90d']);
    let filtered = candles.filter(c => c.timestamp >= cutoff);
    
    // For 5 year period, sample weekly if we have too many points
    if (period === '5y' && filtered.length > 250) {
      const weekly = [];
      for (let i = 0; i < filtered.length; i += Math.ceil(filtered.length / 250)) {
        weekly.push(filtered[i]);
      }
      filtered = weekly;
    }
    
    return filtered;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
  };

  // Calculate percentage change
  const calculateChange = () => {
    if (chartData.length < 2) return null;
    const first = chartData[0].close;
    const last = chartData[chartData.length - 1].close;
    const change = ((last - first) / first) * 100;
    return {
      value: change,
      formatted: `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`,
      isPositive: change >= 0
    };
  };

  const change = calculateChange();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/market-data/portfolio')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{companyName}</h1>
            <p className="text-sm text-gray-500">Stock Price Chart</p>
          </div>
        </div>
      </div>

      {/* Time Period Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-2">
          {timePeriods.map((period) => (
            <button
              key={period.value}
              onClick={() => setSelectedPeriod(period.value)}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                selectedPeriod === period.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading chart data...</span>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && chartData.length > 0 && (
          <>
            {/* Summary Stats */}
            <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Current Price</p>
                <p className="text-xl font-bold text-gray-900">
                  ${chartData[chartData.length - 1]?.close?.toFixed(2) || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Period Change</p>
                <p className={`text-xl font-bold ${change?.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                  {change?.formatted || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">High</p>
                <p className="text-xl font-bold text-gray-900">
                  ${Math.max(...chartData.map(d => d.high)).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Low</p>
                <p className="text-xl font-bold text-gray-900">
                  ${Math.min(...chartData.map(d => d.low)).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Chart */}
            {chartData.length > 0 && (
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10 }}
                      tickFormatter={formatDate}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `$${value.toFixed(0)}`}
                      domain={['dataMin', 'dataMax']}
                      width={60}
                    />
                    <Tooltip
                      formatter={(value) => [`$${parseFloat(value).toFixed(2)}`, 'Close Price']}
                      labelFormatter={(label) => formatDate(label)}
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px',
                        fontSize: '12px',
                        padding: '8px'
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="close"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      name="Close Price"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Data Points Count */}
            <div className="mt-4 text-center text-sm text-gray-500">
              Showing {chartData.length} data points for {timePeriods.find(p => p.value === selectedPeriod)?.label.toLowerCase()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StockChartPage;

